import { useEffect } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  importRefetchInterval,
  isImportTerminal,
  queryKeys,
} from '@splitfiesta/core';
import type {
  CreateImportJobResponseDto,
  ImportJobDto,
  ResolveSplitwiseLabelsRequestDto,
  ResolveSplitwiseLabelsResponseDto,
  SplitwiseImportRequestDto,
  SuccessResponseDto,
} from '@splitfiesta/shared';

import { useTypedApi } from '@/hooks/useTypedApi';

export function useCreateSplitwiseImportMutation() {
  const api = useTypedApi();

  return useMutation({
    mutationFn: async (body: SplitwiseImportRequestDto) => {
      const res = (await api.imports.createSplitwise(
        body
      )) as SuccessResponseDto<CreateImportJobResponseDto>;
      return res.data.jobId;
    },
  });
}

export function useResolveSplitwiseLabelsMutation() {
  const api = useTypedApi();

  return useMutation({
    mutationFn: async (body: ResolveSplitwiseLabelsRequestDto) => {
      const res = (await api.imports.resolveLabels(
        body
      )) as SuccessResponseDto<ResolveSplitwiseLabelsResponseDto>;
      return res.data;
    },
    // The file step waits on this and falls back on any failure, so it must
    // settle at once: no retries, and no pausing when the browser looks offline.
    retry: false,
    networkMode: 'always',
  });
}

export function useImportJobQuery(jobId: string | null) {
  const api = useTypedApi();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.importJob(jobId ?? 'none'),
    queryFn: async () => {
      const res = (await api.imports.getJob(
        jobId as string
      )) as SuccessResponseDto<ImportJobDto>;
      return res.data;
    },
    enabled: !!jobId,
    // Shared with mobile: a blip on top of progress we already painted keeps
    // polling, a dead job id does not.
    refetchInterval: query =>
      importRefetchInterval(
        query.state.status,
        query.state.data as ImportJobDto | undefined,
        query.state.error
      ),
    // The house rule: only a 429 or a 5xx is worth another attempt; a poll
    // that 429s should back off rather than kill the screen, and the job
    // runs on server-side either way.
    retry: (count, error) => {
      const s = (error as { status?: number })?.status;
      return count < 3 && (s === undefined || s === 429 || s >= 500);
    },
    // The query client persists queries, so a restored session would otherwise
    // paint minutes-old progress until the first tick lands.
    staleTime: 0,
    gcTime: 1000 * 60 * 10,
  });

  const terminal = !!query.data && isImportTerminal(query.data.status);

  useEffect(() => {
    // The group only exists once the job says so; refreshing the list any
    // earlier would show nothing and cache the emptiness.
    if (!terminal) return;
    void queryClient.invalidateQueries({ queryKey: queryKeys.groups() });
  }, [jobId, terminal, queryClient]);

  return query;
}

export function useResumeImportMutation() {
  const api = useTypedApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (jobId: string) => {
      await api.imports.resumeJob(jobId);
    },
    onSuccess: (_data, jobId) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.importJob(jobId),
      });
    },
  });
}
